import { createParser } from "nuqs";
import { z } from "zod";

/**
 * Creates a nuqs parser for base64url-encoded JSON objects.
 * Uses Zod v4.1+ codec API for type-safe bidirectional serialization.
 *
 * Parse:     base64url → UTF-8 string → JSON → validated object
 * Serialize: object → JSON string → UTF-8 bytes → base64url
 */
export function createBase64JsonParser<T extends z.ZodType>(
  schema: T,
  eq: (a: z.output<T>, b: z.output<T>) => boolean = (a, b) =>
    JSON.stringify(a) === JSON.stringify(b),
  onParseError?: (error: unknown, rawQuery: string) => void
) {
  // Use Zod's built-in codec to handle base64url encoding/decoding
  const codec = z.codec(z.base64url(), schema, {
    decode: base64urlStr => {
      const bytes = z.util.base64urlToUint8Array(base64urlStr);
      const jsonStr = new TextDecoder().decode(bytes);
      return JSON.parse(jsonStr);
    },
    encode: value => {
      const jsonStr = JSON.stringify(value);
      const bytes = new TextEncoder().encode(jsonStr);
      return z.util.uint8ArrayToBase64url(bytes);
    },
  });

  return createParser<z.output<T>>({
    parse: query => {
      try {
        return codec.parse(query);
      } catch (error) {
        // nuqs expects null for invalid input, not throws
        onParseError?.(error, query);
        return null;
      }
    },
    serialize: value => codec.encode(value),
    eq,
  });
}
